import { useCallback, useEffect, useRef, useState } from 'react';
import { errorAction, type ClientErrorCode } from '../lib/api';
import { navigate, useLocation } from './nav';
import { useSession } from './session';
import { useT } from './i18n';

/* Turns an API error into a redirect (signed out, two-step code, guardian consent, plan) or a message to show. */
export function useErrorHandler() {
  const { t } = useT();
  const { markOut, patchMe } = useSession();
  const loc = useLocation();
  const here = loc.pathname + loc.search;
  return useCallback((err: unknown, noRedirect: ReadonlyArray<ClientErrorCode> = []): string | null => {
    if ((err as Error)?.name === 'AbortError') return null;
    const a = errorAction(err, here, noRedirect);
    if (a.kind === 'redirect') {
      if (a.code === 'signed_out') markOut();
      if (a.code === 'mfa_required') patchMe(m => ({ ...m, mfa: { ...m.mfa, enrolled: true, aal: 'aal1' } }));
      if (a.code === 'guardian_consent_required') patchMe(m => ({ ...m, can_store: false }));
      navigate(a.to, { replace: a.code === 'signed_out' || a.code === 'mfa_required' });
      return null;
    }
    return t(a.key);
  }, [here, markOut, patchMe, t]);
}

export type Load<T> =
  | { status: 'loading'; data?: undefined; message?: undefined }
  | { status: 'ok'; data: T; message?: undefined }
  | { status: 'error'; data?: undefined; message: string };

/* GET something when the page opens (and again when deps change or reload() is called). */
export function useLoad<T>(fn: (signal: AbortSignal) => Promise<T>, deps: unknown[]) {
  const handle = useErrorHandler();
  const [state, setState] = useState<Load<T>>({ status: 'loading' });
  const [n, setN] = useState(0);
  const handleRef = useRef(handle);
  handleRef.current = handle;
  useEffect(() => {
    const ac = new AbortController();
    setState(s => (s.status === 'ok' ? s : { status: 'loading' }));
    fn(ac.signal).then(
      data => { if (!ac.signal.aborted) setState({ status: 'ok', data }); },
      err => {
        if (ac.signal.aborted) return;
        const message = handleRef.current(err);
        if (message) setState({ status: 'error', message });
      });
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, n]);
  const reload = useCallback(() => setN(x => x + 1), []);
  const set = useCallback((data: T) => setState({ status: 'ok', data }), []);
  return { ...state, reload, set };
}

export function useDocumentTitle(title: string) {
  useEffect(() => { document.title = title ? `${title} – PathsAbroad` : 'PathsAbroad'; }, [title]);
}

/* the current time, ticking every `ms` while `on` */
export function useNow(on: boolean, ms = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(id);
  }, [on, ms]);
  return now;
}

/* Move focus to the page heading so screen readers announce the new page or step. The heading may arrive a moment
   later (a lazy-loaded page), and while React swaps a suspended page the old one can still be in the DOM, hidden:
   so look for the visible heading and keep trying briefly until focus really lands. */
export function focusTitle(tries = 30, scroll = false) {
  const el = Array.from(document.querySelectorAll<HTMLElement>('[id="page-title"]')).find(e => e.getClientRects().length > 0);
  if (el) {
    el.focus({ preventScroll: !scroll });
    if (document.activeElement === el) return;
  }
  if (tries > 0) window.setTimeout(() => focusTitle(tries - 1, scroll), 50);
}

/* after a step changes inside a page */
export function focusHeading() {
  requestAnimationFrame(() => focusTitle(30, true));
}
